import { Logo } from 'assets';
import moment from 'moment';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import { notify } from '@/components/shared/notify';

import { getSyncBookListApi } from '@/services/Library/Books';
import { verifyOrderPaymentByHash } from '@/services/Orders';

export interface OrderByHashPaymentDetails {
  count?: number;
  amount?: number;
  method?: string;
  currency?: string;
  rzp_order_id?: string;
  rzp_payment_id?: string;
}

export interface OrderByHashShippingAddress {
  id?: number;
  order_id?: number;
  user_id?: number;
  type?: string;
  name?: string;
  company?: string | null;
  address_1?: string;
  address_2?: string;
  city?: string;
  city_id?: number;
  state?: string;
  state_id?: number;
  country?: string;
  country_id?: number;
  postal_code?: string;
  county_code?: string;
  phone?: string;
  email?: string;
  created_at?: string;
  updated_at?: string;
}

export interface OrderByHashProduct {
  id: number;
  name: string;
  display_name?: string;
  thumbnail_image?: string;
  has_discount?: boolean;
  discount?: string;
  stroked_price?: number;
  main_price?: number;
  coin_price?: number;
  rating?: number;
  description?: string;
  sales?: number;
  type?: string;
  slug?: string;
  category?: string;
  is_paperback?: boolean;
  book_type?: string;
  product_type?: string;
  currency_symbol?: string;
}

export interface OrderByHashItem {
  id: number;
  order_id: number;
  quantity: number;
  product_id: number;
  discounted_price: number;
  discount?: number;
  coupon_code?: string;
  product?: OrderByHashProduct;
}

export interface OrderByHashData {
  id: number;
  code?: string;
  order_code?: string;
  shipping_type?: string;
  payment_details?: OrderByHashPaymentDetails;
  tracking_code?: string | null;
  grand_total: number;
  inr_grand_total?: number;
  currency_symbol?: string;
  country_code?: string;
  currency_code?: string;
  delivery_status?: string;
  payment_type?: string;
  payment_status?: string;
  shipping_cost?: number;
  shipping_address?: OrderByHashShippingAddress;
  order_details?: OrderByHashItem[];
  created_at?: string;
}

export interface GetOrderInfoData {
  order_id?: number;
  order_code?: string;
  user_email?: string;
  user_id?: number;
  user_name?: string;
  grand_total?: number;
  rzp_order_id?: string;
  payment_status?: string;
  payment_type?: string;
  payment_account?: 'razorpay' | 'razorpay_2' | string;
  currency_code?: string;
  expiry_time?: string;
  currency_symbol?: string;
}

export interface ConvertToPrepaidProps {
  orderData: OrderByHashData | null;
  orderInfo?: GetOrderInfoData | null;
  orderInfoExpired?: boolean;
  orderInfoMessage?: string | null;
  hash?: string;
  loading?: boolean;
  error?: string | null;
}

const CashIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
  >
    <rect x='2' y='6' width='20' height='12' rx='2' />
    <circle cx='12' cy='12' r='2.5' />
    <path d='M6 12h.01M18 12h.01' />
  </svg>
);

const LockIcon = ({ className = 'w-3.5 h-3.5 text-[#FFC600]' }: { className?: string }) => (
  <svg
    viewBox='0 0 24 24'
    fill='currentColor'
    className={className}
  >
    <path
      fillRule='evenodd'
      d='M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z'
      clipRule='evenodd'
    />
  </svg>
);

const BackIcon = ({ className = 'w-5 h-5 text-white' }: { className?: string }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
  >
    <path d='M15 18l-6-6 6-6' />
  </svg>
);

export const ConvertToPrepaidContent: React.FC<ConvertToPrepaidProps> = ({
  orderData,
  orderInfo,
  orderInfoExpired = false,
  orderInfoMessage = null,
  hash,
  loading: initialLoading = false,
  error = null,
}) => {
  const router = useRouter();
  const twScreens = useTailwindBreakpoint();
  const isDesktop = twScreens.sm;
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const isProcessingPaymentRef = useRef(isProcessingPayment);

  useEffect(() => {
    isProcessingPaymentRef.current = isProcessingPayment;
  }, [isProcessingPayment]);

  // Prompt confirmation if user tries to close, refresh, navigate away or press back while payment is open
  useEffect(() => {
    if (!isProcessingPayment) return;

    // 1. Tab close / page refresh / browser URL change -> triggers browser default "Leave site?" modal
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isProcessingPaymentRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // 2. Next.js client-side navigation (router.push, Link clicks)
    const handleRouteChangeStart = () => {
      if (!isProcessingPaymentRef.current) return;
      const confirmed = window.confirm(
        'Payment is in progress. Are you sure you want to leave?',
      );
      if (!confirmed) {
        router.events.emit('routeChangeError');
        throw 'Route change aborted: payment in progress.';
      }
    };

    // 3. Browser Back / Forward buttons
    router.beforePopState(() => {
      if (!isProcessingPaymentRef.current) return true;
      const confirmed = window.confirm(
        'Payment is in progress. Are you sure you want to leave?',
      );
      if (!confirmed) {
        window.history.pushState(null, '', window.location.href);
        return false;
      }
      return true;
    });

    window.addEventListener('beforeunload', handleBeforeUnload);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.beforePopState(() => true);
    };
  }, [isProcessingPayment, router]);

  const [paymentStatus, setPaymentStatus] = useState<string>(
    orderInfo?.payment_status || orderData?.payment_status || 'pending',
  );

  useEffect(() => {
    const status = orderInfo?.payment_status || orderData?.payment_status;
    if (status) {
      setPaymentStatus(status);
    }
  }, [orderInfo?.payment_status, orderData?.payment_status]);

  const isPaid = paymentStatus?.toLowerCase() === 'paid';

  // Auto-redirect to library page on payment success (3s countdown with pause on hover/click)
  const REDIRECT_DELAY_MS = 3000;
  const [remainingSeconds, setRemainingSeconds] = useState<number>(3);
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [isRedirectPaused, setIsRedirectPaused] = useState<boolean>(false);
  const remainingMsRef = useRef<number>(REDIRECT_DELAY_MS);
  const lastTickRef = useRef<number>(Date.now());
  const isHoveredRef = useRef<boolean>(false);
  const isPressedRef = useRef<boolean>(false);
  const hasRedirectedRef = useRef<boolean>(false);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isPaid || hasRedirectedRef.current) {
      return;
    }

    lastTickRef.current = Date.now();

    const interval = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;

      // Only deduct time when neither hover active nor clicked active
      if (!isHoveredRef.current && !isPressedRef.current) {
        remainingMsRef.current -= delta;

        if (remainingMsRef.current <= 0) {
          remainingMsRef.current = 0;
          setRemainingSeconds(0);
          setProgressPercent(100);
          clearInterval(interval);
          if (!hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            router.push('/library');
          }
        } else {
          setRemainingSeconds(Math.ceil(remainingMsRef.current / 1000));
          const pct = Math.min(
            100,
            Math.max(
              0,
              ((REDIRECT_DELAY_MS - remainingMsRef.current) / REDIRECT_DELAY_MS) * 100,
            ),
          );
          setProgressPercent(pct);
        }
      }
    }, 50);

    timerIntervalRef.current = interval;

    // Window mouseup listener in case user clicks down and drags outside window
    const handleGlobalMouseUp = () => {
      if (isPressedRef.current) {
        isPressedRef.current = false;
        lastTickRef.current = Date.now();
        setIsRedirectPaused(isHoveredRef.current);
      }
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isPaid, router]);


  const handleMouseEnter = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isHoveredRef.current = true;
    setIsRedirectPaused(true);
  };

  const handleMouseLeave = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isHoveredRef.current = false;
    lastTickRef.current = Date.now();
    setIsRedirectPaused(isPressedRef.current);
  };

  const handleMouseDown = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isPressedRef.current = true;
    setIsRedirectPaused(true);
  };

  const handleMouseUp = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isPressedRef.current = false;
    lastTickRef.current = Date.now();
    setIsRedirectPaused(isHoveredRef.current);
  };

  const handleTouchStart = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isPressedRef.current = true;
    setIsRedirectPaused(true);
  };

  const handleTouchEnd = () => {
    if (!isPaid || remainingMsRef.current <= 0) return;
    isPressedRef.current = false;
    isHoveredRef.current = false;
    lastTickRef.current = Date.now();
    setIsRedirectPaused(false);
  };

  const [isExpired, setIsExpired] = useState<boolean>(orderInfoExpired);
  const [expiredMessage, setExpiredMessage] = useState<string>(
    orderInfoMessage || 'Time limit to convert order to prepaid has expired',
  );

  useEffect(() => {
    if (orderInfoExpired) {
      setIsExpired(true);
      if (orderInfoMessage) {
        setExpiredMessage(orderInfoMessage);
      }
    }
  }, [orderInfoExpired, orderInfoMessage]);

  // Order ID calculation from order-by-hash API
  const displayOrderId = useMemo(() => {
    const id =
      orderData?.id ||
      (orderData as any)?.order_id ||
      orderInfo?.order_id;
    return id ? String(id) : '';
  }, [orderData, orderInfo]);

  // Countdown timer based on orderInfo.expiry_time
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number }>({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (orderInfoExpired) {
      setIsExpired(true);
      if (orderInfoMessage) {
        setExpiredMessage(orderInfoMessage);
      }
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    let targetTime: number;

    if (orderInfo?.expiry_time) {
      targetTime = new Date(orderInfo.expiry_time).getTime();
    } else {
      const timerStorageKey = `zl_prepaid_timer_${displayOrderId || 'default'}`;
      const saved = localStorage.getItem(timerStorageKey);
      if (saved && !isNaN(Number(saved)) && Number(saved) > Date.now()) {
        targetTime = Number(saved);
      } else {
        targetTime = Date.now() + (55 * 60 + 13) * 1000;
        localStorage.setItem(timerStorageKey, String(targetTime));
      }
    }

    const updateTimer = () => {
      const now = Date.now();
      const diffInSeconds = Math.floor((targetTime - now) / 1000);

      if (diffInSeconds <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
        setExpiredMessage('Time limit to convert order to prepaid has expired');
        return;
      }

      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;

      setTimeLeft({ hours, minutes, seconds });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [orderInfo?.expiry_time, orderInfoExpired, orderInfoMessage, displayOrderId]);

  const formatPrice = (val?: number | string) => {
    const num = Number(val || 0);
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const currencySymbol =
    orderInfo?.currency_symbol || orderData?.currency_symbol || '₹';

  // Placed date formatting
  const formattedDate = useMemo(() => {
    const dateStr =
      orderData?.created_at ||
      orderData?.shipping_address?.created_at;
    return dateStr ? moment(dateStr).format('DD MMM YYYY, hh:mm A') : '';
  }, [orderData]);

  // Items calculation
  const items = orderData?.order_details || [];

  // Total and Subtotal calculation
  const subtotal = useMemo(() => {
    if (orderInfo?.grand_total !== undefined) return orderInfo.grand_total;
    if (orderData?.grand_total !== undefined) return orderData.grand_total;
    if (items.length > 0) {
      return items.reduce(
        (acc, item) => acc + (item.discounted_price || 0) * (item.quantity || 1),
        0,
      );
    }
    return 0;
  }, [orderInfo, orderData, items]);

  const totalAmount =
    orderInfo?.grand_total ?? orderData?.grand_total ?? subtotal;

  const shippingCost = orderData?.shipping_cost ?? 0;

  // Preload Razorpay checkout script (matches checkout-v2:L2156)
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Razorpay Handle matching checkout-v2 implementation (lines 916-969)
  const razorpayHandle = async ({
    key,
    amount,
    currency,
    name,
    image,
    order_id,
    handler,
    modal,
    prefill,
    onPaymentFailed,
  }: {
    key: string;
    amount: number;
    currency: string;
    name: string;
    image: any;
    order_id?: string;
    handler: any;
    modal: any;
    prefill: any;
    onPaymentFailed?: (response: any) => void;
  }) => {
    try {
      if (typeof window !== 'undefined') {
        const options: any = {
          key: key,
          amount: amount,
          currency: currency,
          name: name,
          image: image,
          handler: handler,
          modal: modal,
          prefill: prefill,
        };

        if (order_id) {
          options.order_id = order_id;
        }

        if ((window as any).Razorpay) {
          const rzp = new (window as any).Razorpay(options);

          rzp.on('payment.failed', function (response: any) {
            console.error('Razorpay Payment Failed: ', response.error);
            notify(
              response.error?.description ||
                'Payment Failed. Please try again.',
              'error',
            );
            if (onPaymentFailed) {
              onPaymentFailed(response);
            }
          });

          rzp.open();
        } else {
          console.error('Razorpay SDK not loaded');
          notify('Payment gateway is loading. Please try again.', 'error');
          setIsProcessingPayment(false);
        }
      }
    } catch (error: any) {
      console.error(error);
      notify(error?.message || 'Payment initialization failed', 'error');
      setIsProcessingPayment(false);
    }
  };

  // Razorpay Payment Handler matching checkout-v2
  const handlePayOnline = async () => {
    try {
      if (isPaid) {
        notify('This order has already been paid.', 'info');
        return;
      }

      if (isExpired) {
        notify(
          expiredMessage || 'Time limit to convert order to prepaid has expired',
          'error',
        );
        return;
      }

      setIsProcessingPayment(true);

      // Key selection: prefer orderInfo.payment_account, fallback to orderData.payment_type
      const paymentAccount = orderInfo?.payment_account || orderData?.payment_type;
      const rzpKey =
        paymentAccount === 'razorpay_2'
          ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2 || process.env.NEXT_PUBLIC_RAZORPAY_KEY
          : process.env.NEXT_PUBLIC_RAZORPAY_KEY || process.env.NEXT_PUBLIC_RAZORPAY_KEY_2;

      if (!rzpKey) {
        notify('Payment gateway key is not configured.', 'error');
        setIsProcessingPayment(false);
        return;
      }

      const rzpOrderId =
        orderInfo?.rzp_order_id || orderData?.payment_details?.rzp_order_id;
      const isTestKey = rzpKey.startsWith('rzp_test_');

      // In development with test key, pass order_id if present from orderInfo
      const shouldPassOrderId =
        Boolean(rzpOrderId) &&
        (!isTestKey || Boolean(orderInfo?.rzp_order_id));

      const chargeAmount =
        orderInfo?.grand_total || orderData?.grand_total || totalAmount;

      const amountToCharge = shouldPassOrderId
        ? chargeAmount
        : Math.round(Number(chargeAmount) * 100);

      await razorpayHandle({
        key: rzpKey,
        amount: amountToCharge,
        currency: orderInfo?.currency_code || orderData?.currency_code || 'INR',
        name: 'ZebraLearn',
        image: Logo,
        order_id: shouldPassOrderId ? rzpOrderId : undefined,
        prefill: {
          name:
            orderInfo?.user_name ||
            orderData?.shipping_address?.name ||
            '',
          email:
            orderInfo?.user_email ||
            orderData?.shipping_address?.email ||
            '',
          contact: orderData?.shipping_address?.phone || '',
        },
        modal: {
          escape: false,
          ondismiss: () => {
            setIsProcessingPayment(false);
          },
        },
        handler: async (response: any) => {
          try {
            if (hash) {
              await verifyOrderPaymentByHash(hash, {
                rzp_payment_id: response?.razorpay_payment_id,
                razorpay_payment_id: response?.razorpay_payment_id,
                razorpay_order_id: response?.razorpay_order_id || rzpOrderId,
                razorpay_signature: response?.razorpay_signature,
              });
            }
          } catch (verifyErr) {
            console.error('Payment verification failed:', verifyErr);
          }

          setIsProcessingPayment(false);
          setPaymentStatus('paid');
          hasRedirectedRef.current = false;
          remainingMsRef.current = REDIRECT_DELAY_MS;
          setRemainingSeconds(REDIRECT_DELAY_MS / 1000);
          setProgressPercent(0);
          isHoveredRef.current = false;
          isPressedRef.current = false;
          setIsRedirectPaused(false);
          lastTickRef.current = Date.now();
          getSyncBookListApi();
          notify(
            'Payment successful! Your order has been converted to prepaid.',
            'success',
          );
        },
        onPaymentFailed: () => {
          setIsProcessingPayment(false);
        },
      });
    } catch (err: any) {
      console.error('Payment initialization error:', err);
      notify(err?.message || 'Failed to initiate payment', 'error');
      setIsProcessingPayment(false);
    }
  };

  if (initialLoading) {
    return (
      <div className='min-h-screen bg-black flex items-center justify-center text-white px-4'>
        <div className='flex flex-col items-center gap-3'>
          <div className='w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin' />
          <p className='text-sm text-grayZl-400'>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className='min-h-screen bg-black flex items-center justify-center text-white px-4'>
        <div className='max-w-md w-full bg-grayZl-950 border border-grayZl-850 rounded-2xl p-6 sm:p-8 text-center'>
          <div className='w-12 h-12 rounded-full bg-error-500/10 border border-error-500/20 text-error-500 flex items-center justify-center mx-auto mb-4 text-xl font-bold'>
            !
          </div>
          <h2 className='text-lg sm:text-xl font-bold text-white mb-2'>
            {error || 'Order Not Found'}
          </h2>
          <p className='text-xs sm:text-sm text-grayZl-400 mb-6'>
            We couldn&#39;t find the order associated with this link. Please check your link or contact support.
          </p>
          <button
            onClick={() => router.push('/')}
            className='w-full py-3 px-4 rounded-xl bg-grayZl-850 hover:bg-grayZl-800 text-white text-sm font-semibold transition'
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const shipping = orderData?.shipping_address;

  return (
    <div className='min-h-screen bg-black text-white selection:bg-primary selection:text-white'>
      <Head>
        <title>Order Payment #{displayOrderId} | ZebraLearn</title>
        <meta name='description' content={`Payment for Order #${displayOrderId}`} />
      </Head>

      {/* Main Container - max 1080px as requested */}
      <div className='max-w-[1080px] w-full mx-auto px-4 sm:px-6 py-5 sm:py-8 md:py-10'>
        {/* Mobile Top Navigation */}
        <div className='flex items-center justify-between md:hidden mb-4'>
          <button
            onClick={() => router.back()}
            className='p-1 -ml-1 text-white hover:text-primary transition-colors'
            aria-label='Go back'
          >
            <BackIcon />
          </button>
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${
              isPaid
                ? 'bg-emerald-950/60 border border-emerald-600/50 text-emerald-400'
                : 'bg-[#2A1408] border border-[#5A240A] text-primary'
            }`}
          >
            {isPaid ? (
              <>
                <svg
                  className='w-3.5 h-3.5 text-emerald-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth='2.5'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                </svg>
                <span>Paid</span>
              </>
            ) : (
              <>
                <CashIcon className='w-3.5 h-3.5' />
                <span>{orderData?.payment_type ? orderData.payment_type.replace(/_/g, ' ') : 'Cash on Delivery'}</span>
              </>
            )}
          </div>
        </div>

        {/* Header Section */}
        <div className='flex flex-col md:flex-row md:items-start md:justify-between gap-3'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold tracking-tight text-white'>
              <span className='hidden md:inline'>Order Payment </span>
              <span className='md:hidden inline'>Order ID </span>
              <span className='text-primary'>#{displayOrderId}</span>
            </h1>
            <p className='text-xs sm:text-sm text-grayZl-500 mt-1 font-normal'>
              Placed on {formattedDate}
            </p>
          </div>

          {/* Desktop Right Badges */}
          <div className='hidden md:flex items-center gap-2.5'>
            <div
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold capitalize ${
                isPaid
                  ? 'bg-emerald-950/40 border border-emerald-800/40 text-emerald-300'
                  : 'bg-[#2A1408] border border-[#5A240A] text-primary'
              }`}
            >
              {!isPaid && <CashIcon className='w-4 h-4' />}
              <span>{isPaid ? 'Prepaid' : (orderData?.payment_type ? orderData.payment_type.replace(/_/g, ' ') : 'Cash on Delivery')}</span>
            </div>
            <div
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-xs sm:text-sm font-medium capitalize ${
                isPaid
                  ? 'bg-emerald-950/60 border border-emerald-600/50 text-emerald-400'
                  : isExpired
                  ? 'bg-amber-950/30 border-amber-800/40 text-amber-400'
                  : 'bg-grayZl-850 border-grayZl-800 text-grayZl-300'
              }`}
            >
              {isPaid ? (
                <>
                  <svg
                    className='w-3.5 h-3.5 text-emerald-400'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    strokeWidth='2.5'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                  </svg>
                  <span>Paid</span>
                </>
              ) : isExpired ? (
                <span>Expired</span>
              ) : (
                <span>{orderData?.delivery_status || paymentStatus || 'Pending'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Banner: "Get Instant Benefits" or Expired Error Message - hidden when already paid */}
        {!isPaid && (
          isExpired ? (
            <div className='w-full rounded-2xl bg-[#1C0F0A] border border-[#5A240A] p-4 sm:p-5 mt-5 sm:mt-6 flex items-start sm:items-center gap-3.5 shadow-sm'>
              <div className='w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0 text-amber-400'>
                <svg
                  className='w-5 h-5 text-amber-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='12' />
                  <line x1='12' y1='16' x2='12.01' y2='16' />
                </svg>
              </div>
              <div className='flex flex-col'>
                <h3 className='text-sm sm:text-base font-semibold text-white'>Time Limit Expired</h3>
                <p className='text-xs sm:text-sm text-grayZl-400 mt-0.5 leading-snug'>
                  {expiredMessage || 'Time limit to convert order to prepaid has expired'}
                </p>
              </div>
            </div>
          ) : (
            <div className='w-full rounded-2xl bg-[#140E0A] border border-[#3E1C0F] p-4 sm:p-5 mt-5 sm:mt-6'>
              <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
                {/* Desktop Left: Get Instant Benefits Heading */}
                <div className='hidden md:block flex-shrink-0'>
                  <h2 className='text-xl lg:text-2xl font-bold text-white tracking-tight'>
                    Get <span className='text-primary'>Instant Benefits</span>
                  </h2>
                  <p className='text-xs sm:text-sm text-grayZl-400 mt-1'>
                    Pay online now for a faster and smoother delivery.
                  </p>
                </div>

                {/* Middle: Benefits Columns */}
                <div className='flex items-center gap-4 sm:gap-6 divide-x divide-[#3E1C0F]'>
                  <div className='flex flex-col justify-center'>
                    <p className='text-xs sm:text-sm font-semibold text-white'>
                      Instant Digital Access
                    </p>
                    <p className='text-[11px] sm:text-xs text-grayZl-400 mt-0.5 leading-snug'>
                      Get eBook immediately after payment.
                    </p>
                  </div>

                  <div className='flex flex-col justify-center pl-4 sm:pl-6'>
                    <p className='text-xs sm:text-sm font-semibold text-white'>
                      Faster Delivery
                    </p>
                    <p className='text-[11px] sm:text-xs text-grayZl-400 mt-0.5 leading-snug'>
                      Shipped earlier with priority support.
                    </p>
                  </div>
                </div>

                {/* Right: Countdown Timer */}
                <div className='flex flex-col items-start md:items-end justify-center pt-2 md:pt-0 border-t md:border-t-0 border-[#3E1C0F]/60 md:pl-4'>
                  <span className='text-[10px] sm:text-xs font-semibold text-grayZl-500 uppercase tracking-wider mb-1'>
                    OFFER ENDS IN
                  </span>
                  <div className='flex items-center gap-2'>
                    {/* Hours Box */}
                    <div className='flex flex-col items-center justify-center bg-black/70 border border-grayZl-850 rounded-lg min-w-[40px] sm:min-w-[44px] py-1 px-1.5 shadow-sm'>
                      <span className='text-primary font-bold text-base sm:text-lg leading-tight font-mono'>
                        {String(timeLeft.hours).padStart(2, '0')}
                      </span>
                      <span className='text-[9px] font-semibold text-grayZl-500 tracking-wider mt-0.5'>
                        HRS
                      </span>
                    </div>

                    {/* Minutes Box */}
                    <div className='flex flex-col items-center justify-center bg-black/70 border border-grayZl-850 rounded-lg min-w-[40px] sm:min-w-[44px] py-1 px-1.5 shadow-sm'>
                      <span className='text-primary font-bold text-base sm:text-lg leading-tight font-mono'>
                        {String(timeLeft.minutes).padStart(2, '0')}
                      </span>
                      <span className='text-[9px] font-semibold text-grayZl-500 tracking-wider mt-0.5'>
                        MINS
                      </span>
                    </div>

                    {/* Seconds Box */}
                    <div className='flex flex-col items-center justify-center bg-black/70 border border-grayZl-850 rounded-lg min-w-[40px] sm:min-w-[44px] py-1 px-1.5 shadow-sm'>
                      <span className='text-primary font-bold text-base sm:text-lg leading-tight font-mono'>
                        {String(timeLeft.seconds).padStart(2, '0')}
                      </span>
                      <span className='text-[9px] font-semibold text-grayZl-500 tracking-wider mt-0.5'>
                        SECS
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Main Content Grid: 2 Columns on Desktop */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-5 mt-5 sm:mt-6'>
          {/* Left Column: Order Items & Delivery Address */}
          <div className='lg:col-span-7 flex flex-col gap-5'>
            {/* Card 1: Order Items */}
            <div className='bg-grayZl-950 border border-grayZl-850 rounded-2xl p-4 sm:p-5'>
              <h3 className='font-semibold text-sm sm:text-base text-white mb-3 sm:mb-4'>
                Order Items ({items.length || 1})
              </h3>

              <div className='divide-y divide-grayZl-850/60'>
                {items.length > 0 ? (
                  items.map((item, idx) => {
                    const productTitle =
                      item?.product?.display_name ||
                      item?.product?.name ||
                      'Fundraising Decoded Combo [eBook + Hardcopy]';
                    const imgUrl = item?.product?.thumbnail_image;
                    const itemPrice = item?.discounted_price || item?.product?.main_price || subtotal;

                    return (
                      <div key={item.id || idx} className='flex items-center gap-3.5 sm:gap-4 py-3 first:pt-0 last:pb-0'>
                        <div className='w-14 h-16 sm:w-16 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden bg-grayZl-900 border border-grayZl-850 flex items-center justify-center relative'>
                          {imgUrl ? (
                            <Image
                              src={imgUrl}
                              alt={productTitle}
                              fill
                              sizes='64px'
                              className='object-cover'
                            />
                          ) : (
                            <div className='text-grayZl-600 text-xs font-medium'>Book</div>
                          )}
                        </div>

                        <div className='flex flex-col justify-center min-w-0 flex-1'>
                          <p className='font-medium text-xs sm:text-sm text-white line-clamp-2 leading-snug'>
                            {productTitle}
                          </p>
                          <p className='text-xs sm:text-sm text-grayZl-500 mt-1 font-normal'>
                            QTY: {item.quantity || 1} × {currencySymbol}
                            {formatPrice(itemPrice)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className='flex items-center gap-3.5 sm:gap-4 py-1'>
                    <div className='w-14 h-16 sm:w-16 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden bg-grayZl-900 border border-grayZl-850 flex items-center justify-center'>
                      <span className='text-grayZl-600 text-xs font-medium'>Book</span>
                    </div>
                    <div className='flex flex-col justify-center min-w-0 flex-1'>
                      <p className='font-medium text-xs sm:text-sm text-white line-clamp-2 leading-snug'>
                        Fundraising Decoded Combo [eBook + Hardcopy]
                      </p>
                      <p className='text-xs sm:text-sm text-grayZl-500 mt-1 font-normal'>
                        QTY: 1 × {currencySymbol}1,690.00
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Card 2: Delivery Address */}
            <div className='bg-grayZl-950 border border-grayZl-850 rounded-2xl p-4 sm:p-5'>
              <h3 className='font-semibold text-sm sm:text-base text-white mb-3 sm:mb-4'>
                Delivery Address
              </h3>

              <div className='flex flex-col text-xs sm:text-sm'>
                <p className='font-semibold text-white'>
                  {shipping?.name || 'Customer Name'}
                </p>

                {(shipping?.phone || shipping?.email) && (
                  <p className='text-grayZl-400 mt-1 flex flex-wrap items-center gap-1.5'>
                    {shipping?.phone && <span>{shipping.phone}</span>}
                    {shipping?.phone && shipping?.email && <span className='text-grayZl-600'>•</span>}
                    {shipping?.email && <span className='break-all'>{shipping.email}</span>}
                  </p>
                )}

                <p className='text-grayZl-400 mt-1.5 leading-relaxed'>
                  {[
                    shipping?.address_1,
                    shipping?.address_2,
                    shipping?.city,
                    shipping?.state
                      ? `${shipping.state}${shipping?.postal_code ? ` -${shipping.postal_code}` : ''}`
                      : shipping?.postal_code,
                    shipping?.country,
                  ]
                    .filter(Boolean)
                    .join(' , ') || 'Address not available'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Payment Summary */}
          <div className='lg:col-span-5'>
            <div className='bg-grayZl-950 border border-grayZl-850 rounded-2xl p-4 sm:p-5 flex flex-col justify-between h-full'>
              <div>
                <h3 className='font-semibold text-sm sm:text-base text-white mb-4 sm:mb-5'>
                  Payment Summary
                </h3>

                {/* Subtotal */}
                <div className='flex items-center justify-between py-1'>
                  <span className='text-xs sm:text-sm text-grayZl-400'>Subtotal</span>
                  <span className='text-xs sm:text-sm text-white font-medium'>
                    {currencySymbol}{formatPrice(subtotal)}
                  </span>
                </div>

                {/* Shipping cost */}
                <div className='flex items-center justify-between py-1 mt-1'>
                  <span className='text-xs sm:text-sm text-grayZl-400'>Shipping cost</span>
                  <span className='text-xs sm:text-sm text-success-500 font-medium'>
                    {shippingCost === 0 ? 'Free' : `${currencySymbol}${formatPrice(shippingCost)}`}
                  </span>
                </div>

                {/* Divider */}
                <div className='border-t border-grayZl-850 my-4' />

                {/* Total amount */}
                <div className='flex items-center justify-between py-1 mb-4 sm:mb-5'>
                  <span className='font-semibold text-sm sm:text-base text-white'>Total amount</span>
                  <span className='font-bold text-xl sm:text-2xl text-white tracking-tight'>
                    {currencySymbol}{formatPrice(totalAmount)}
                  </span>
                </div>
              </div>

              {/* Action Button & Badges */}
              <div>
                <button
                  type='button'
                  onClick={(e) => {
                    if (isPaid) {
                      e.preventDefault();
                    } else {
                      handlePayOnline();
                    }
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                  disabled={isProcessingPayment || (!isPaid && isExpired)}
                  style={
                    isPaid
                      ? {
                          background: `linear-gradient(to right, rgba(0, 0, 0, 0.28) ${progressPercent}%, rgba(0, 0, 0, 0) ${progressPercent}%), #059669`,
                        }
                      : undefined
                  }
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-sm sm:text-base text-center shadow-lg transition-all duration-150 flex items-center justify-center gap-2 select-none overflow-hidden ${
                    isPaid
                      ? 'hover:brightness-95 active:scale-[0.98] text-white shadow-emerald-900/30 cursor-pointer'
                      : isExpired
                      ? 'bg-grayZl-850 text-grayZl-500 border border-grayZl-800 cursor-not-allowed shadow-none'
                      : 'bg-primary hover:bg-darkPrimary active:scale-[0.98] text-white shadow-primary/20 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
                  }`}
                >
                  {isProcessingPayment ? (
                    <div className='flex items-center gap-2'>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                      <span>Processing...</span>
                    </div>
                  ) : isPaid ? (
                    <div className='flex flex-col items-center justify-center text-center py-0.5 leading-tight'>
                      <div className='flex items-center justify-center gap-2 text-white'>
                        <svg
                          className='w-5 h-5 text-white flex-shrink-0'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                          strokeWidth='2.5'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                        <span className='font-semibold text-sm sm:text-base'>
                          {remainingSeconds > 0
                            ? `Redirecting to library page in ${remainingSeconds}s`
                            : 'Redirecting to library page...'}
                        </span>
                      </div>
                      <span className='text-[11px] sm:text-xs text-emerald-100/90 font-normal mt-0.5'>
                        {isRedirectPaused
                          ? isDesktop
                            ? 'Paused • Hover outside to continue'
                            : 'Paused • Release to continue'
                          : isDesktop
                          ? 'Hover to pause redirecting'
                          : 'Click to pause redirecting'}
                      </span>
                    </div>
                  ) : isExpired ? (
                    <span>Time Limit Expired</span>
                  ) : (
                    <span>Pay Online & Convert to Prepaid</span>
                  )}
                </button>

                {/* Trust Badges */}
                <div className='mt-3.5 flex flex-col items-center justify-center text-center'>
                  <div className='flex items-center justify-center gap-1.5 text-xs text-grayZl-400'>
                    {isPaid ? (
                      <>
                        <svg
                          className='w-4 h-4 text-emerald-400'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                          strokeWidth='2.5'
                        >
                          <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                        </svg>
                        <span className='text-emerald-400 font-medium'>Payment Completed</span>
                      </>
                    ) : isExpired ? (
                      <span className='text-amber-400 font-medium'>Offer has expired</span>
                    ) : (
                      <>
                        <LockIcon className='w-3.5 h-3.5 text-[#FFC600]' />
                        <span>100% Secure Payment via Razorpay</span>
                      </>
                    )}
                  </div>
                  <p className='text-[11px] text-grayZl-500 mt-1'>
                    {isPaid
                      ? 'Your order has been converted to prepaid'
                      : isExpired
                      ? 'This order will be delivered with Cash on Delivery'
                      : 'Cards, UPI, Netbanking, & Wallets supported'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertToPrepaidContent;
